import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, Users, ShieldCheck, MapPin, PiggyBank, PlayCircle } from "lucide-react";
import { APP_REGISTER_URL, APP_LINK_PROPS, track } from "@/lib/config";
import HeroAppPreview from "@/components/site/landing/HeroAppPreview";

/**
 * Hero sans framer-motion pour garder ce composant hors du bundle framer-motion
 * (chargé en différé avec les sections lazy). Animations remplacées par CSS Tailwind.
 */
export default function HeroPrimary() {
    return (
        <section className="relative isolate overflow-hidden">
            <div className="absolute inset-0 -z-10 pointer-events-none">
                <div className="hidden sm:block absolute top-[-30%] left-[10%] w-[40rem] h-[40rem] rounded-full bg-gradient-to-br from-primary-300/40 via-primary-200/30 to-transparent blur-3xl" />
                <div className="hidden sm:block absolute top-[10%] right-[-5%] w-[36rem] h-[36rem] rounded-full bg-gradient-to-tl from-primary-400/30 via-primary-300/20 to-transparent blur-3xl" />
                <div className="absolute inset-0 bg-grid-indigo opacity-40 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 lg:pt-24 lg:pb-20">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
                <div className="lg:col-span-6 text-center lg:text-left">
                <div className="animate-fade-in">
                    <Badge
                        data-testid="hero-badge"
                        className="bg-white border border-primary-200 text-primary-800 hover:bg-white rounded-full px-4 py-1.5 shadow-sm font-medium"
                    >
                        <Users className="w-3.5 h-3.5 mr-1.5 text-primary-600" />
                        Conçu pour les syndics bénévoles
                    </Badge>
                </div>

                <h1
                    data-testid="hero-title"
                    className="animate-fade-in-up mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-[-0.045em] leading-[1.05] sm:leading-[1.02] text-slate-900 max-w-xl mx-auto lg:mx-0"
                    style={{ animationDelay: "50ms" }}
                >
                    Gérez enfin votre copropriété{" "}
                    <span className="relative inline-block whitespace-nowrap">
                        <span className="relative z-10 bg-gradient-to-br from-primary-700 via-primary-600 to-primary-500 bg-clip-text text-transparent">
                            sereinement
                        </span>
                        <span className="absolute -bottom-1 left-0 right-0 h-3 bg-primary-200/70 -z-0 rounded" />
                        .
                    </span>
                    <br className="hidden sm:block" />
                    Sans mauvaise surprise.
                </h1>

                <p
                    className="animate-fade-in-up mt-7 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-xl mx-auto lg:mx-0"
                    style={{ animationDelay: "100ms" }}
                >
                    Assemblée générale, comptabilité, courriers légaux : TyCopro vous guide à chaque étape et sécurise tout. Vous gardez le contrôle et vous <span className="font-semibold text-slate-900">économisez des milliers d'euros</span> par an.
                </p>

                <div
                    className="animate-fade-in-up mt-10 flex flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-4"
                    style={{ animationDelay: "180ms" }}
                >
                    <div className="relative group">
                        <div
                            className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-primary-500 via-primary-400 to-primary-600 opacity-60 blur-xl group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                        />
                        <Button
                            asChild
                            data-testid="hero-cta-primary"
                            className="relative bg-gradient-to-br from-primary-700 to-primary-600 hover:from-primary-600 hover:to-primary-500 text-white rounded-2xl h-14 px-8 text-base sm:text-lg font-medium shadow-[0_18px_40px_-12px_rgba(67,56,202,0.6)] hover:-translate-y-0.5 transition-all duration-300"
                        >
                            <a
                                href={APP_REGISTER_URL}
                                {...APP_LINK_PROPS}
                                onClick={() => track("cta_clicked", { cta: "hero_primary" })}
                            >
                                Démarrer
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </a>
                        </Button>
                    </div>
                    <Button
                        data-testid="hero-cta-secondary"
                        variant="ghost"
                        onClick={() => document.getElementById("comparatif")?.scrollIntoView({ behavior: "smooth" })}
                        className="text-primary-800 hover:bg-primary-50 hover:text-primary-900 rounded-2xl h-14 px-6 border border-primary-200 hover:border-primary-300 text-base"
                    >
                        <PlayCircle className="w-4 h-4 mr-2" />
                        Voir le comparatif
                    </Button>
                </div>

                <p className="mt-4 text-sm text-slate-500">
                    Essai gratuit de 2 semaines · Sans carte bancaire · Sans engagement · <strong className="text-slate-900">9,50 €/mois/lot TTC</strong>
                </p>

                <div
                    className="animate-fade-in mt-10 flex flex-wrap items-center justify-center lg:justify-start gap-2.5 sm:gap-3"
                    style={{ animationDelay: "350ms" }}
                >
                    {[
                        { ic: PiggyBank, t: "Au moins 2× moins cher qu'un syndic pro", hot: true },
                        { ic: ShieldCheck, t: "100 % conforme à la loi" },
                        { ic: MapPin, t: "Vos données en France" },
                    ].map((p, i) => {
                        const Ic = p.ic;
                        return (
                            <span
                                key={i}
                                data-testid={p.hot ? "hero-proof-price" : undefined}
                                className={`inline-flex items-center gap-1.5 text-xs sm:text-sm backdrop-blur rounded-full px-3 py-1.5 shadow-sm ${
                                    p.hot
                                        ? "bg-primary-700 border border-primary-700 text-white font-semibold shadow-primary-700/25"
                                        : "bg-white/70 border border-slate-200 text-slate-700"
                                }`}
                            >
                                <Ic className={`w-3.5 h-3.5 ${p.hot ? "text-primary-200" : "text-primary-600"}`} />
                                {p.t}
                            </span>
                        );
                    })}
                </div>
                </div>

                <div className="lg:col-span-6">
                    <HeroAppPreview />
                </div>
                </div>
            </div>
        </section>
    );
}
